/**
 * The pipeline in the user's words (spec 2026-09-16 §2.3).
 *
 * One module names the stages for every surface that lists them — the video
 * page's progress list while a job runs, and the Details dialog's "How it was
 * made" tab afterwards. The `id`s are the server's `currentStage` values and
 * never reach the screen; the labels are what a person who has never heard of
 * a speech model or a render engine would say the step does. No engine names,
 * ever: the test greps for them.
 *
 * Pure and DOM-free on purpose — vitest runs `web/src/lib/**` in node. Icons
 * belong to the component that draws the list, not here.
 */

export type PipelineSourceKind = "url" | "upload";

export interface PipelineStage {
  /** Matches the server's `currentStage`. Never shown. */
  id: string;
  /** Plain-language label. */
  label: string;
}

/** The order the pipeline runs in — stable, and what `pipelineStageIndex` walks. */
export const PIPELINE_STAGE_IDS = [
  "yt-dlp",
  "transcribe",
  "detectEnd",
  "translate",
  "render",
  "generateSocialCaption",
] as const;

/**
 * @param captionLanguage  The channel's caption language display name
 *   ("Arabic", "French", …). An English source has nothing to translate, so
 *   the stage is simply "Captions"; blank falls back to the same.
 * @param sourceKind  "url" for a YouTube job (the first step downloads it),
 *   "upload" when the user brought the file (the first step normalises it).
 */
export function pipelineStages(captionLanguage: string, sourceKind: PipelineSourceKind): PipelineStage[] {
  const lang = captionLanguage.trim();
  const plainCaptions = lang.length === 0 || lang.toLowerCase() === "english";
  return [
    { id: "yt-dlp", label: sourceKind === "upload" ? "Prepare your video" : "Get the video" },
    { id: "transcribe", label: "Write down what is said" },
    { id: "detectEnd", label: "Find where the thought ends" },
    { id: "translate", label: plainCaptions ? "Captions" : `Captions in ${lang}` },
    { id: "render", label: "Build the vertical video" },
    { id: "generateSocialCaption", label: "Write the post caption" },
  ];
}

/** Which kind of job this is, from what the job payload carries. A job with
 *  a source URL came from YouTube; anything else was uploaded. */
export function pipelineSourceKind(
  input: { url?: string } | undefined,
  sourceUrl?: string,
): PipelineSourceKind {
  return input?.url || sourceUrl ? "url" : "upload";
}

/**
 * Where the job sits in the list, so a renderer can tick what ran, spin what
 * is running and mute what is still to come:
 *
 *   - ready            → `stages.length` (everything done)
 *   - failed/incomplete→ the index of the stage it stopped on (0 when unknown)
 *   - anything else    → the index of `currentStage` (0 when unknown)
 *
 * Stages before the index are done; the stage AT the index is running (or the
 * one that failed); stages after it have not run.
 */
export function pipelineStageIndex(
  stages: ReadonlyArray<PipelineStage>,
  status: string,
  currentStage: string | undefined,
): number {
  if (status === "ready") return stages.length;
  const idx = currentStage ? stages.findIndex((s) => s.id === currentStage) : -1;
  return idx >= 0 ? idx : 0;
}
